/**
 * Enterprise-grade paginated list hook
 * 
 * Provides:
 * - Automatic pagination management
 * - Loading and error states
 * - Retry logic
 * - Empty state handling
 * - Error suppression for expected cases (404 on empty lists)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from './useToast';
import { handleError, shouldShowError, type ErrorContext } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';
import type { PaginatedResponse } from '@/types/common';

export interface PaginationParams {
  page: number;
  size: number;
  sort?: string;
  [key: string]: unknown;
}

export interface UsePaginatedListOptions<T> {
  fetchFn: (params: PaginationParams) => Promise<PaginatedResponse<T>>;
  entityName?: string;
  initialPage?: number;
  initialSize?: number;
  enabled?: boolean;
  onSuccess?: (data: PaginatedResponse<T>) => void;
  onError?: (error: unknown) => void;
  suppress404Errors?: boolean; // For empty database scenarios
}

export interface UsePaginatedListResult<T> {
  data: T[];
  isLoading: boolean;
  error: unknown | null;
  pagination: {
    page: number;
    size: number;
    totalPages: number;
    totalElements: number;
  };
  setPage: (page: number) => void;
  setSize: (size: number) => void;
  refetch: () => Promise<void>;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * Hook for managing paginated list data
 */
export function usePaginatedList<T>(
  options: UsePaginatedListOptions<T>
): UsePaginatedListResult<T> {
  const {
    fetchFn,
    entityName = 'items',
    initialPage = 0,
    initialSize = 20,
    enabled = true,
    onSuccess,
    onError,
    suppress404Errors = true,
  } = options;

  const [data, setData] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown | null>(null);
  const [page, setPage] = useState(initialPage);
  const [size, setSize] = useState(initialSize);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const { toast } = useToast();
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async () => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();

    try {
      setIsLoading(true);
      setError(null);

      const params: PaginationParams = {
        page,
        size,
      };

      const response = await fetchFn(params);

      // Check if request was aborted
      if (abortControllerRef.current.signal.aborted) {
        return;
      }

      // Update state - ensure we always have an array, even if content is undefined
      const content = response.content || [];
      const totalElements = response.page?.totalElements ?? 0;
      const totalPages = response.page?.totalPages ?? 0;
      
      setData(content);
      setTotalPages(totalPages);
      setTotalElements(totalElements);
      setError(null); // Clear any previous errors

      // Call success callback
      if (onSuccess) {
        onSuccess(response);
      }

      // Log success
      logger.debug(`Fetched ${entityName}`, {
        count: content.length,
        total: totalElements,
        page: response.page?.number || 0,
      });
    } catch (err: unknown) {
      // Check if request was aborted
      if (abortControllerRef.current.signal.aborted) {
        return;
      }

      const errorContext: ErrorContext = {
        operation: 'list',
        entity: entityName,
      };

      const handledError = handleError(err, errorContext);

      // For list endpoints with suppress404Errors, treat any error as empty data
      // This handles cases where the database is empty (404, 500, or any other error)
      if (suppress404Errors) {
        // Treat any error as empty data, not an error
        setData([]);
        setTotalPages(0);
        setTotalElements(0);
        setError(null); // Clear error state

        // Log for debugging but don't show to user
        logger.debug(`Empty list for ${entityName} (error suppressed)`, {
          entity: entityName,
          operation: 'list',
          errorCategory: handledError.category,
          statusCode: handledError.statusCode,
        });

        // Call success callback with empty data
        if (onSuccess) {
          onSuccess({
            content: [],
            page: {
              size: 0,
              number: 0,
              totalElements: 0,
              totalPages: 0,
            },
          } as PaginatedResponse<T>);
        }
      } else {
        // Reset state on actual error
        setData([]);
        setTotalPages(0);
        setTotalElements(0);
        setError(err);

        // Show error toast if appropriate
        if (shouldShowError(handledError, errorContext)) {
          toast({
            variant: 'destructive',
            title: 'Error',
            description: handledError.userMessage,
          });
        }

        // Call error callback
        if (onError) {
          onError(err);
        }
      }
    } finally {
      if (!abortControllerRef.current.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [fetchFn, page, size, enabled, entityName, onSuccess, onError, suppress404Errors, toast]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchData();

    // Cleanup: abort request on unmount
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData]);

  const refetch = useCallback(async () => {
    await fetchData();
  }, [fetchData]);

  return {
    data,
    isLoading,
    error,
    pagination: {
      page,
      size,
      totalPages,
      totalElements,
    },
    setPage,
    setSize,
    refetch,
    hasNextPage: page < totalPages - 1,
    hasPreviousPage: page > 0,
  };
}






